import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Modal, Form, Button, InputGroup } from "react-bootstrap";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import CurrencyCustomInput from "../../utils/CurrencyCustomInput";
import ExpensesSelector from "../../utils/ExpensesSelector";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { useBackendUrl } from "../../../utils/hooks";
import Select from "react-select";
import { nanoid } from "nanoid";

const settlementSourceOptions = [
  { label: "Vendor Account", value: "vendor" },
  { label: "Incentive Account", value: "incentive" },
];

function SettleIncentiveModal({
  show,
  onClose,
  incentive,
  month,
  year,
  onSuccess,
  settleAll = false,
  totalAmount = 0,
}) {
  const backendUrl = useBackendUrl();
  const [showJournalPopover, setShowJournalPopover] = useState(false);

  const formik = useFormik({
    initialValues: {
      AccountID: "",
      Description: "",
      Type: "",
      amount: settleAll ? totalAmount || 0 : incentive?.remaining || 0,
      handleSettlementBy: "",
    },
    enableReinitialize: true,
    validate: values => {
      const errors = {};
      if (!values.handleSettlementBy) {
        errors.handleSettlementBy = "Select how to settle";
      }
      if (!values.AccountID) errors.AccountID = "Select account";
      if (!values.amount || Number(values.amount) <= 0)
        errors.amount = "Enter a valid amount";
      return errors;
    },
    onSubmit: values => {
      const payload = {
        AccountID: values.AccountID,
        AccountName: values.Description,
        amount: Number(values.amount),
        Month: month,
        Year: year,
        SettleAll: settleAll,
        handleSettlementBy: values.handleSettlementBy,
      };
      if (!settleAll) {
        payload.transId = incentive?.transId;
        payload.idempotencyKey = `${payload.transId}_${nanoid()}`;
      }

      console.log(payload);
      settleIncentiveMutation.mutate(payload);
    },
  });

  const handleSelectedAccount = account => {
    formik.setFieldValue("AccountID", account?.AccountID || "");
    formik.setFieldValue("Description", account?.Description || "");
    formik.setFieldValue("Type", account?.Type || "");
    setShowJournalPopover(false);
  };

  const settleIncentive = async payload => {
    let response = await fetch(`${backendUrl}/api/incentive/settlements`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || "Unable to settle incentive");
    }

    return response.json();
  };

  const settleIncentiveMutation = useMutation(settleIncentive, {
    onSuccess: ({ message }) => {
      toast.success(message || "Incentive settled successfully");
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: error => {
      toast.error(
        `Unable to settle incentive: ${error?.message || "Please try again"}`
      );
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setShowJournalPopover(false);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="md"
      animation={false}
      autoFocus={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {settleAll
            ? `Settle all incentives for ${month}/${year}`
            : "Settle Incentive"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          {settleAll ? (
            <div className="mb-3">
              <Form.Label>Total Due Amount</Form.Label>
              <Form.Control
                readOnly
                value={Number(totalAmount).toLocaleString("en-NG", {
                  style: "currency",
                  currency: "NGN",
                  maximumFractionDigits: 2,
                })}
                className="bg-lighter"
              />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <Form.Label>#</Form.Label>
                <Form.Control
                  readOnly
                  value={incentive?.transId || "—"}
                  className="bg-lighter"
                />
              </div>

              <div className="mb-3">
                <Form.Label>Current Balance</Form.Label>
                <Form.Control
                  readOnly
                  value={
                    incentive?.remaining !== undefined &&
                    incentive?.remaining !== null
                      ? Number(incentive.remaining).toLocaleString("en-NG", {
                          style: "currency",
                          currency: "NGN",
                          maximumFractionDigits: 2,
                        })
                      : "—"
                  }
                  className="bg-lighter"
                />
              </div>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Settle By</Form.Label>
            <Select
              options={settlementSourceOptions}
              value={settlementSourceOptions.find(
                el => el.value === formik.values.handleSettlementBy
              )}
              onChange={selected =>
                formik.setFieldValue("handleSettlementBy", selected?.value)
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Corresponding Account</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                placeholder="Select Corresponding Account"
                value={
                  formik.values.AccountID
                    ? `${formik.values.AccountID} / ${
                        formik.values.Description
                      } / ${formik.values.Type}`
                    : ""
                }
                onChange={() => {}}
                readOnly
                isInvalid={
                  formik.touched.AccountID && !!formik.errors.AccountID
                }
              />
              <Popover
                isOpen={showJournalPopover}
                onClickOutside={() => setShowJournalPopover(false)}
                content={() => (
                  <ExpensesSelector
                    handleSelectedExpense={handleSelectedAccount}
                    usage="chart of accounts"
                    onHide={() => setShowJournalPopover(false)}
                    // Type="CASH AND CASH EQUIVALENT"
                  />
                )}
                position="bottom"
              >
                <InputGroup.Text onClick={() => setShowJournalPopover(true)}>
                  <MagnifyIcon />
                </InputGroup.Text>
              </Popover>
              <Form.Control.Feedback type="invalid">
                {formik.errors.AccountID}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <CurrencyCustomInput
              name="amount"
              value={formik.values.amount}
              onValueChange={(value, name) => {
                formik.setFieldValue(name, value);
              }}
              onBlur={() => formik.setFieldTouched("amount", true)}
              isInvalid={formik.touched.amount && !!formik.errors.amount}
            />
            {formik.touched.amount && formik.errors.amount ? (
              <div className="invalid-feedback d-block">
                {formik.errors.amount}
              </div>
            ) : null}
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="white"
              type="button"
              onClick={onClose}
              disabled={settleIncentiveMutation.isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={settleIncentiveMutation.isLoading}>
              {settleIncentiveMutation.isLoading
                ? "Processing..."
                : settleAll
                ? "Settle All Incentives"
                : "Settle Incentive"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default SettleIncentiveModal;
